import { IncidentType, TyreLocation, UserType, ClientType } from "../shared/enums";
import { ClientLink, Driver, IncidentClientLink, Vehicle } from "./client";
import type { ChatEscalation } from './chat';

export class IncidentTyre {
    tyreLocation: TyreLocation;
    tyreNumber: number;
    size: string;
    sizeDescription: string;
    qty: number;


    constructor(tyreLocation: TyreLocation, tyreNumber: number, size: string = '') {
        this.tyreLocation = tyreLocation;
        this.tyreNumber = tyreNumber;
        if (size) {
            this.size = size;
        }
        this.qty = 1;
    }

}

export class IncidentLocation {
  incidentId?: number | null;
  latitude: number;
  longitude: number;
  description: string | null = null;
  descriptionShort: string;

}

export class IncidentOperator {
    operatorId: number = 0;
    name: string = '';
    clientId: number = 0;
    userType: UserType = UserType.unknown;
    isActive: boolean;
    cellNumber: string = '';
}

export class CallcentreOperatorOption {
    userId: number;
    fullName: string;
}

export class Document {
    documentId: number;
    incidentId: number;
    description: string;
    url: string;
    documentClassificationId: number | null;
    createdByUserId: number;
    createdByUserName: string;
    createdDate: Date;
    subCategoryId?: number;
    mimeType?: string | null;
    mediaKind?: string | null;
    localBase64?: string;
}


export class IncidentFieldFlags {
  clientId: boolean = false;
  clientName: boolean = false;
  driver: boolean = false;
  fleetVehicle: boolean = false;
  incidentType: boolean = false;
  tyrePositions: boolean = false;
  tyreBrands: boolean = false;
  location: boolean = false;
  incidentDescription: boolean = false;
  fleetVehicleOdoReading: boolean = false;
}

export class Incident {
    chatEscalations: ChatEscalation[] = [];
    allowFleetChat: boolean = false;
    private static readonly minimumRequiredFieldCount = 8;

    incidentId: number = 0;
    callcentreOperatorId: number | null = null;
    incidentNumber: string = '';
    statusId: number = 0;
    status: string = '';
    incidentType: IncidentType = IncidentType.Tyre;
    tyrePositions: IncidentTyre[] = [];
    tyreBrand: number = 0;
    tyreBrands: number[] = [];
    tyreBrandsText: string = '';
    location: IncidentLocation;
    driver: Driver;
    fleetVehicle: Vehicle;
    dtLogged: Date;
    incidentOperators: IncidentOperator[] = [];
    clientId: number = 0;
    clientName: string;
    clientWhatsappGroupLink: string | null = null;
    clientWhatsappGroupName: string | null = null;
    transporterName: string | null = null;
    incidentClients: IncidentClientLink[] = [];
    availableCallcentreOperators: CallcentreOperatorOption[] = [];
    breakdownTechnician: Driver;
    documents: Document[] = [];
    incidentDescription: string;
    fleetVehicleOdoReading: number;
    breakdownTechnicianDriverEta: Date;
    initialTravelTimeMinutes: number | null;
    originalEta: Date;
    orderNumber: string;
    dtCompleted: Date | null;
    workDone: string;
    comment: string;
    incidentActions: IncidentActions | null = null;
    incidentDelays: IncidentDelay[] = [];

    public get hasOperator(): boolean {
        return this.incidentOperators.length > 0;
    }

    public get callcentreName(): string {
        const ccLink = this.incidentClients.find(link => link.connectionTypeId === 2);
        return ccLink ? ccLink.clientName : '';
    }

    public get mainCallCentre(): IncidentClientLink | undefined {
        return this.incidentClients.find(link => link.connectionTypeId === 2 && link.isActive);
    }

    public get serviceProviderName(): string {
        console.log("GETTING SP NAME");
        
        const spLink = this.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive);
        console.log(spLink);
        
        return spLink ? spLink.clientName : '';
    }

    public get serviceProviderDriverName(): string {
        const spLink = this.incidentOperators.find(link => link.userType === 5 && link.isActive);
        return spLink ? spLink.name : '';
    }

    public get hasServiceProviderInvoice(): boolean {
        return this.documents.some(doc => doc.documentClassificationId === 4); // 4 = Invoice external
    }

    /**
     * Counts how many of the minimum required incident fields currently have values.
     * Strings must be non-empty after trimming, numbers must be greater than zero,
     * and array-backed fields count only when they contain at least one valid item.
     */
    public get populatedMinimumRequiredFieldCount(): number {
        const firstTyrePosition = this.tyrePositions?.[0];

        return [
            Incident.hasStringValue(this.transporterName),
            Incident.hasStringValue(this.fleetVehicle?.registrationNumber),
            Incident.hasStringValue(firstTyrePosition?.size),
            Incident.hasValidNumberValue(firstTyrePosition?.tyreLocation),
            this.tyreBrands?.some(brandId => Incident.hasValidNumberValue(brandId)) ?? false,
            Incident.hasStringValue(this.driver?.phoneNumber),
            Incident.hasStringValue(this.incidentDescription),
            Incident.hasStringValue(this.orderNumber),
        ].filter(Boolean).length;
    }

    /**
     * Displays the minimum required field progress as "x of 8".
     */
    public get populatedMinimumRequiredFieldsText(): string {
        return `${this.populatedMinimumRequiredFieldCount} of ${Incident.minimumRequiredFieldCount}`;
    }

    private static hasStringValue(value: string | null | undefined): boolean {
        return value !== null && value !== undefined && value.trim().length > 0;
    }

    private static hasValidNumberValue(value: number | null | undefined): boolean {
        return value !== null && value !== undefined && value !== 0;
    }

  }
  
export class IncidentSearchRequest {
  closedIncidents: boolean;
  startDate: string;
  endDate: string;
  incidentNumber: string;

}

export class IncidentCloseRequest {
  incidentId: number;
  orderNumber: string;
  dateCompleted: Date;
  workDone: string;
  comment: string;
}

export class UploadingFile {
  name: string;
  base64: string;
  incidentId: number;
  createdByUserId: number;
  documentClassificationId: number;
  description?: string;
  subCategoryId?: number;
}

export class IncidentDelay {
  delayMinutes: number;
  delayReason: string;
}

export class IncidentDelayRequest extends IncidentDelay {
    incident: Incident;
    
}

export class EtaConfirm {
    etaMinutes: number;
    delayMinutes: number;
    incidentId: number;
}

export class IncidentEtaUpdate {
    incidentId: number;
    newEta: Date;
}

export class DelayAdded {
    incidentId: number;
    delayMinutes: number;
    delayReason: string;
}

export class ServiceProviderStatusUpdate {
    clientId: number;
    userId: number;
    serviceProviderStatusId: number;
}

export class BDTechIncidentStatusUpdate {
    incidentId: number;
    statusId: number;
    userId: number;
    minutesAdded: number;
}


export interface IncidentTableColumn {
  key: string;
  title: string;
  width?: string;
}

export interface IncidentColumnConfig {
  key: string;
  title: string;
  width?: string;
  visibleFor: ClientType[];
}

export const INCIDENT_TABLE_COLUMNS: IncidentColumnConfig[] = [
  { 
    key: 'incidentNo', 
    title: 'Fleet Controller',  
    width: '10%',
    visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  },
  { 
    key: 'dt', 
    title: 'Logged', 
    width: '15%', 
    visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  },
  { 
    key: 'vehicleAndDriver', 
    title: 'Vehicle / Driver', 
    width: '15%', 
    visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  },
  { 
    key: 'status', 
    title: 'Status', 
    width: '12%', 
    visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  },
  { 
    key: 'serviceProvider', 
    title: 'Service Provider', 
    width: '12%', 
    visibleFor: [ClientType.fleet, ClientType.call_centre] 
  },
  { 
    key: 'eta', 
    title: 'ETA', 
    width: '10%', 
    visibleFor: [ClientType.call_centre, ClientType.service_provider] 
  },
  // {
  //   key: 'pow', 
  //   title: 'POW', 
  //   width: '15%',  
  //   visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  // },
  // {
  //   key: 'spinvoice', 
  //   title: 'SP Invoice', 
  //   width: '15%',  
  //   visibleFor: [ClientType.call_centre, ClientType.service_provider] 
  // },
  // {
  //   key: 'updatepo', 
  //   title: 'Update PO', 
  //   width: '15%',  
  //   visibleFor: [ClientType.call_centre, ClientType.service_provider] 
  // },
  // {
  //   key: 'reinvoice', 
  //   title: 'RE Invoice', 
  //   width: '15%',  
  //   visibleFor: [ClientType.call_centre] 
  // },
  // {
  //   key: 'invoice', 
  //   title: 'Invoice', 
  //   width: '15%',  
  //   visibleFor: [ClientType.fleet,ClientType.call_centre] 
  // },
  { 
    key: 'actions', 
    title: 'Actions', 
    width: '10%', 
    visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] 
  },
];

export class NearbyServiceProvider {
  within25km: number;
  within50km: number;
  within75km: number;
  within100km: number;
  within150km: number;
  within200km: number;
  within250km: number;
}

export const INCIDENT_TABLE_ABRIDGED_COLUMNS: IncidentColumnConfig[] = [
      { key: 'incidentNo', title: 'Fleet Controller', width: '10%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      // { key: 'fleet', title: 'Fleet', width: '15%', visibleFor: [ClientType.call_centre, ClientType.service_provider] },
      { key: 'vehicleAndDriver', title: 'Vehicle', width: '15%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      { key: 'serviceProvider', title: 'Service Provider', width: '15%', visibleFor: [ClientType.fleet, ClientType.call_centre] },
      { key: 'status', title: 'Status', width: '12%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      { key: 'eta', title: 'ETA', width: '10%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      { key: 'timeElapsed', title: 'Time Elapsed', width: '12%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
    ];

export const INCIDENT_TABLE_CLOSED_COLUMNS: IncidentColumnConfig[] = [
      { key: 'incidentNo', title: 'No.', width: '10%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      {key: 'dt',title: 'Logged',width: '15%',visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      { key: 'vehicleAndDriver', title: 'Vehicle', width: '15%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider] },
      { key: 'serviceProvider', title: 'Service Provider', width: '15%', visibleFor: [ClientType.fleet, ClientType.call_centre] },
      {key: 'pow', title: 'POW', width: '15%', visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider]},
      {key: 'spinvoice', title: 'SP Invoice',width: '15%',visibleFor: [ClientType.call_centre, ClientType.service_provider]},
      {key: 'updatepo',title: 'Update PO',width: '15%',visibleFor: [ClientType.call_centre, ClientType.service_provider]},
      {key: 'reinvoice',title: 'RE Invoice', width: '15%',visibleFor: [ClientType.call_centre] },
      {key: 'invoice', title: 'Invoice', width: '15%', visibleFor: [ClientType.fleet,ClientType.call_centre] },
      {key: 'actions',title: 'Actions',width: '12%',visibleFor: [ClientType.fleet, ClientType.call_centre, ClientType.service_provider]},
    ];

// export class ClosedIncident extends Incident {
//   incidentActions: IncidentActions | null = null;
// }

export class IncidentActions {
  dtCallcentreAsked: Date | null;
  dtCallcentreAssigned: Date | null;
  dtSPAsked: Date | null;
  dtSPAssigned: Date | null;
  dtSPAccepted: Date | null;
  dtSPRejected: Date | null;
  dtSPUnassigned: Date | null;
  dtTechAsked: Date | null;
  dtTechAssigned: Date | null;
  dtTechConfirmedEta: Date | null;
  dtSPDelayAdded: Date | null;
  dtTechDelayAdded: Date | null;
  dtTechViewedIncident: Date | null;
  dtTechSentInitialLocation: Date | null;
  dtTechArrived: Date | null;
  dtCompleted: Date | null;
  dtWorkReportAssigned: Date | null;
  dtClosed: Date | null;
  dtSPInvoiceUploaded: Date | null;
  dtPOUpdated: Date | null;
  dtReInvoiced: Date | null;
  dtInvoiceUploaded: Date | null;
  dtWAMessageCopiedSP: Date | null;
  dtWAMessageCopiedFleet: Date | null;

}

export interface EtaBasedThresholds {
  /** Minutes after breakdownTechnicianDriverEta before the state turns orange. 0 = warn immediately when ETA passes. */
  overdueWarnMinutes: number;
  /** Minutes after breakdownTechnicianDriverEta before the state turns red. */
  overdueCriticalMinutes: number;
}

export interface IncidentStateStep {
  /**
   * One or more date field names on `Incident` (refSource='incident') or `IncidentActions` (refSource='incidentActions').
   * The step is considered completed when ANY of these fields has a non-null date.
   * The most recent non-null date among them is used as the step's timestamp for comparison.
   */
  completedFields: (keyof Incident | keyof IncidentActions)[];
  refSource: 'incident' | 'incidentActions';
  /** Label shown in the status column while waiting for the NEXT step. */
  awaitingLabel: string;
  /** Minutes after this step's date before the state turns orange. Unused when etaBasedThresholds is set. */
  warnMinutes: number;
  /** Minutes after this step's date before the state turns red. Unused when etaBasedThresholds is set. */
  criticalMinutes: number;
  /** When set, severity is computed from breakdownTechnicianDriverEta instead of the step's completedField date. */
  etaBasedThresholds?: EtaBasedThresholds;
}

export interface IncidentCurrentState {
  awaitingLabel: string;
  /** The timestamp of the last completed action (used as the reference clock for display). */
  referenceTime: Date;
  minutesElapsed: number;
  severity: 'normal' | 'warn' | 'critical';
  /**
   * Present only for ETA-based states.
   * Positive = minutes remaining until ETA; negative = minutes past ETA (overdue).
   */
  etaMinutesRemaining?: number;
}

/**
 * Ordered list of timeline steps.
 * Each entry describes what it means to have completed that step (completedField has a date)
 * but NOT the next one — i.e. we are "awaiting" the next step.
 * Adjust warnMinutes and criticalMinutes to match business SLAs.
 */
export const INCIDENT_TIMELINE_STATES: IncidentStateStep[] = [
  {
    completedFields: ['dtLogged'],
    refSource: 'incident',
    awaitingLabel: 'Awaiting SP Allocation',
    warnMinutes: 5,
    criticalMinutes: 10,
  },
  {
    completedFields: ['dtSPAssigned'],
    refSource: 'incidentActions',
    awaitingLabel: 'Awaiting Tech/SP Acceptance',
    warnMinutes: 2,
    criticalMinutes: 5,
  },
  {
    completedFields: ['dtSPRejected', 'dtSPUnassigned'],
    refSource: 'incidentActions',
    awaitingLabel: 'Awaiting SP Re-allocation',
    warnMinutes: 5,
    criticalMinutes: 10,
  },
  {
    completedFields: ['dtTechAssigned', 'dtSPAccepted'],
    refSource: 'incidentActions',
    awaitingLabel: 'Awaiting ETA Confirmation',
    warnMinutes: 2,
    criticalMinutes: 5,
  },
  {
    completedFields: ['dtTechConfirmedEta'],
    refSource: 'incidentActions',
    awaitingLabel: 'Awaiting Tech/SP Arrival',
    warnMinutes: 0,
    criticalMinutes: 0,
    etaBasedThresholds: {
      overdueWarnMinutes: 0,
      overdueCriticalMinutes: 10,
    },
  },
  {
    completedFields: ['dtTechArrived'],
    refSource: 'incidentActions',
    awaitingLabel: 'Awaiting Completion',
    warnMinutes: 45,
    criticalMinutes: 60,
  },
];

export class DashboardStats {
  totalBreakdownsToday: number;
  totalCancelledIncidentsToday: number;
  activeBreakdowns: number;
}

export class IncidentLogRequest {
  incidentId: number;
  logTypeId: number;
  comment: string;
}
